import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Radio, Volume2, VolumeX, Pause, Play, SkipForward, ExternalLink, CloudSun, Clock, ArrowRight, ShieldCheck } from "lucide-react";
import api from "@/lib/api";

const SEGMENT_MS = 9000;
const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString("fr-FR", { day: "2-digit", month: "long", year: "numeric" }) : "");

const CLOCKS = [
  { label: "Paris", tz: "Europe/Paris", flag: "🇫🇷" },
  { label: "Sydney", tz: "Australia/Sydney", flag: "🇦🇺" },
  { label: "Toronto", tz: "America/Toronto", flag: "🇨🇦" },
];

export const WteLiveChannel = () => {
  const [feed, setFeed] = useState(null);
  const [idx, setIdx] = useState(0);
  const [paused, setPaused] = useState(false);
  const [voice, setVoice] = useState(false);
  const [now, setNow] = useState(new Date());
  const audioRef = useRef(null);

  useEffect(() => {
    api.get("/live/feed").then((r) => setFeed(r.data)).catch(() => setFeed(null));
    const t = setInterval(() => setNow(new Date()), 1000);
    const refresh = setInterval(() => api.get("/live/feed").then((r) => setFeed(r.data)).catch(() => {}), 600000);
    return () => { clearInterval(t); clearInterval(refresh); };
  }, []);

  const segments = useMemo(() => {
    if (!feed) return [];
    const s = [];
    s.push({ kind: "intro", eyebrow: "WTE LIVE", title: "L'actualité de l'immigration Australie & Canada", text: "Mises à jour officielles, actualités, météo et informations utiles aux candidats — en continu." });
    (feed.visa_updates?.canada || []).slice(0, 3).forEach((i) => s.push({ kind: "item", eyebrow: "🇨🇦 Mise à jour immigration", ...i }));
    (feed.visa_updates?.australie || []).slice(0, 3).forEach((i) => s.push({ kind: "item", eyebrow: "🇦🇺 Mise à jour immigration", ...i }));
    (feed.news || []).slice(0, 4).forEach((i) => s.push({ kind: "item", eyebrow: i.country === "canada" ? "🇨🇦 Actualité" : "🇦🇺 Actualité", ...i }));
    if (feed.weather?.length) s.push({ kind: "weather", eyebrow: "Météo des destinations", title: "Quel temps fait-il là-bas, maintenant ?", weather: feed.weather });
    s.push({ kind: "packs", eyebrow: "Nos formules", title: "Un accompagnement clair, du dossier à la finalisation", text: "Pack Visa, Préparation au départ, Installation à l'arrivée : choisissez la formule adaptée à votre projet." });
    s.push({ kind: "cta", eyebrow: "Consultation offerte", title: "30 minutes avec un conseiller, gratuitement", text: "Faites le point sur votre projet Australie ou Canada, sans engagement." });
    return s;
  }, [feed]);

  const seg = segments[idx % (segments.length || 1)];

  useEffect(() => {
    if (paused || segments.length === 0) return;
    const t = setTimeout(() => setIdx((v) => (v + 1) % segments.length), SEGMENT_MS);
    return () => clearTimeout(t);
  }, [idx, paused, segments.length]);

  // Voix off : génère (et met en cache côté serveur) la lecture du segment courant.
  useEffect(() => {
    if (!voice || !seg) return;
    const text = [seg.title, seg.summary || seg.text].filter(Boolean).join(". ");
    let cancelled = false;
    api.post("/live/tts", { text })
      .then(({ data }) => {
        if (cancelled || !audioRef.current) return;
        audioRef.current.src = `${process.env.REACT_APP_BACKEND_URL}${data.url}`;
        audioRef.current.play().catch(() => {});
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [voice, seg]);

  if (!feed) return null;
  const breaking = feed.breaking?.length ? feed.breaking : [...(feed.visa_updates?.canada || []), ...(feed.visa_updates?.australie || [])].slice(0, 4);

  return (
    <section data-testid="wte-live-channel" className="relative overflow-hidden bg-navy">
      <div className="pointer-events-none absolute -top-40 right-0 h-[30rem] w-[30rem] rounded-full bg-gold/10 blur-[120px]" />
      <div className="relative z-10 mx-auto max-w-6xl px-5 py-12 sm:px-8 sm:py-16">
        {/* Barre de chaîne */}
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <span className="inline-flex items-center gap-2 rounded-full bg-red-600 px-3 py-1 text-[11px] font-bold uppercase tracking-[0.18em] text-white">
              <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-white" /> Live
            </span>
            <span className="font-heading text-2xl text-white sm:text-3xl">WTE <span className="text-gold">LIVE</span></span>
            <span className="hidden text-[11px] uppercase tracking-[0.2em] text-white/50 sm:inline">Chaîne d'information Australie &amp; Canada</span>
          </div>
          <div className="flex items-center gap-2">
            <button data-testid="live-pause-btn" onClick={() => setPaused((p) => !p)} title={paused ? "Reprendre" : "Pause"}
              className="rounded-full border border-white/20 p-2 text-white/80 transition-colors hover:border-gold hover:text-gold">
              {paused ? <Play size={15} /> : <Pause size={15} />}
            </button>
            <button data-testid="live-next-btn" onClick={() => setIdx((v) => (v + 1) % segments.length)} title="Segment suivant"
              className="rounded-full border border-white/20 p-2 text-white/80 transition-colors hover:border-gold hover:text-gold">
              <SkipForward size={15} />
            </button>
            <button data-testid="live-voice-btn" onClick={() => setVoice((v) => !v)} title={voice ? "Couper la voix off" : "Activer la voix off"}
              className={`inline-flex items-center gap-2 rounded-full border px-3 py-2 text-xs transition-colors ${voice ? "border-gold bg-gold/15 text-gold" : "border-white/20 text-white/80 hover:border-gold hover:text-gold"}`}>
              {voice ? <Volume2 size={15} /> : <VolumeX size={15} />} Voix off
            </button>
          </div>
        </div>

        {/* Écran */}
        <div className="mt-6 overflow-hidden rounded-2xl border border-white/10 bg-white/[0.04]">
          <div className="relative min-h-[260px] p-7 sm:min-h-[280px] sm:p-10">
            <AnimatePresence mode="wait">
              <motion.div key={idx} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -14 }} transition={{ duration: 0.6 }}>
                <span className="text-[11px] uppercase tracking-[0.26em] text-gold">{seg?.eyebrow}</span>
                <h2 data-testid="live-segment-title" className="mt-3 max-w-3xl font-heading text-2xl leading-snug text-white sm:text-3xl">{seg?.title}</h2>
                {(seg?.summary || seg?.text) && (
                  <p className="mt-4 max-w-2xl text-sm leading-relaxed text-white/80 sm:text-base">{seg.summary || seg.text}</p>
                )}

                {seg?.kind === "item" && (
                  <div className="mt-5 flex flex-wrap items-center gap-x-5 gap-y-2 text-xs text-white/60">
                    <span className="inline-flex items-center gap-1.5">
                      {seg.official && <ShieldCheck size={13} className="text-gold" />}
                      Source : <span className="text-white/85">{seg.source_name}</span>
                    </span>
                    <span>Publié le {fmtDate(seg.published_at)}</span>
                    <span>Mis à jour le {fmtDate(seg.updated_at)}</span>
                    {seg.source_url && (
                      <a href={seg.source_url} target="_blank" rel="noopener noreferrer nofollow" data-testid="live-source-link"
                        className="inline-flex items-center gap-1 text-gold hover:underline">
                        Source officielle <ExternalLink size={12} />
                      </a>
                    )}
                  </div>
                )}

                {seg?.kind === "weather" && (
                  <div className="mt-6 grid gap-3 sm:grid-cols-4">
                    {seg.weather.map((w) => (
                      <div key={w.city} className="rounded-xl border border-white/10 bg-white/[0.05] px-4 py-3">
                        <p className="text-xs uppercase tracking-widest text-white/50">{w.country === "canada" ? "🇨🇦" : "🇦🇺"} {w.city}</p>
                        <p className="mt-1 flex items-center gap-2 font-heading text-2xl text-white"><CloudSun size={18} className="text-gold" /> {w.temperature}°C</p>
                        <p className="text-[11px] text-white/60">{w.label}</p>
                      </div>
                    ))}
                  </div>
                )}

                {(seg?.kind === "packs" || seg?.kind === "cta") && (
                  <div className="mt-7 flex flex-wrap gap-3">
                    <Link to={seg.kind === "packs" ? "/nos-packs" : "/rendez-vous"} data-testid={`live-cta-${seg.kind}`}
                      className="group inline-flex items-center gap-2 rounded-full bg-gold px-6 py-3 text-xs font-bold uppercase tracking-[0.1em] text-navy transition-colors hover:bg-gold-hover">
                      {seg.kind === "packs" ? "Voir nos packs" : "Réserver ma consultation gratuite"}
                      <ArrowRight size={15} className="transition-transform group-hover:translate-x-1" />
                    </Link>
                  </div>
                )}
              </motion.div>
            </AnimatePresence>

            {/* Horloges */}
            <div className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-2 text-[11px] uppercase tracking-[0.18em] text-white/50">
              <Clock size={13} className="text-gold" />
              {CLOCKS.map((c) => (
                <span key={c.tz}>{c.flag} {c.label} {now.toLocaleTimeString("fr-FR", { timeZone: c.tz, hour: "2-digit", minute: "2-digit" })}</span>
              ))}
            </div>
          </div>

          {/* Bandeau breaking news */}
          {breaking.length > 0 && (
            <div className="flex items-stretch border-t border-white/10 bg-navy-light/60">
              <span className="flex shrink-0 items-center bg-gold px-4 text-[10px] font-bold uppercase tracking-[0.18em] text-navy">
                <Radio size={12} className="mr-1.5" /> À la une
              </span>
              <div className="relative flex-1 overflow-hidden py-3">
                <div className="flex w-max animate-[ticker_48s_linear_infinite] gap-10 whitespace-nowrap px-5 text-xs text-white/80">
                  {[...breaking, ...breaking].map((b, i) => (
                    <Link key={`${b.slug}-${i}`} to={`/wte-live/${b.slug}`} className="transition-colors hover:text-gold">
                      <span className="text-gold">●</span> {b.title}
                    </Link>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Progression des segments */}
          <div className="flex gap-1 px-5 pb-4">
            {segments.map((s, i) => (
              <button key={i} onClick={() => setIdx(i)} aria-label={`Segment ${i + 1}`} data-testid={`live-dot-${i}`}
                className={`h-[3px] flex-1 rounded-full transition-colors ${i === idx ? "bg-gold" : "bg-white/15 hover:bg-white/35"}`} />
            ))}
          </div>
        </div>

        <div className="mt-5 flex flex-wrap items-center justify-between gap-3">
          <p className="text-[11px] text-white/45">
            Informations sourcées et datées. Les mises à jour officielles renvoient vers la source gouvernementale concernée.
          </p>
          <Link to="/wte-live" data-testid="live-all-link" className="inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.16em] text-gold hover:underline">
            Toutes les informations <ArrowRight size={13} />
          </Link>
        </div>
      </div>
      <audio ref={audioRef} className="hidden" />
    </section>
  );
};
